import { useEffect, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
} from "firebase/firestore";
import {
  FaInstagram,
  FaTiktok,
  FaTwitch,
  FaTwitter,
  FaYoutube,
} from "react-icons/fa";
import {
  AlertTriangle,
  Briefcase,
  Clock,
  Eye,
  Gamepad2,
  HeartHandshake,
  Loader2,
  MessageCircle,
  Play,
  Sparkles,
  Trophy,
  Workflow,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../Context/AuthContext";
import { db } from "../Context/firebaseConfig";
import SEO, { breadcrumbSchema, profilePageSchema } from "./SEO";
import { getUserEntitlements } from "../config/plans";
import { normalizeSocialUrl } from "../utils/socialLinks";
import {
  queueTransactionalEmail,
  transactionalEmails,
} from "../utils/transactionalEmail";
import { listPublicProfiles } from "../services/publicProfiles";

const MINT = "#10b981";
const PURPLE = "#a855f7";

const QUESTION_CARDS = [
  {
    key: "questionOne",
    icon: Workflow,
    accent: MINT,
    label: "What things would make the editing gig the easiest for you?",
  },
  {
    key: "questionTwo",
    icon: HeartHandshake,
    accent: PURPLE,
    label: "How can you make the editing gig easiest for the other person?",
  },
  {
    key: "questionThree",
    icon: AlertTriangle,
    accent: "#f59e0b",
    label: "What are the pain points others may have while working with you?",
  },
  {
    key: "questionFour",
    icon: Trophy,
    accent: MINT,
    label: "What do you bring to the table?",
  },
];

const SOCIALS = [
  { key: "youtube", label: "YouTube", icon: FaYoutube },
  { key: "twitch", label: "Twitch", icon: FaTwitch },
  { key: "instagram", label: "Instagram", icon: FaInstagram },
  { key: "twitter", label: "X / Twitter", icon: FaTwitter },
  { key: "tiktok", label: "TikTok", icon: FaTiktok },
];

function SocialButton({ href, label, icon: Icon, platform }) {
  const socialUrl = normalizeSocialUrl(platform, href);
  if (!socialUrl) return null;

  return (
    <a
      href={socialUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="grid h-11 w-11 place-items-center rounded-2xl border border-white/10 bg-white/[0.04] text-white/65 transition hover:-translate-y-0.5 hover:border-white/20 hover:bg-white/[0.07] hover:text-white"
    >
      <Icon className="h-4 w-4" />
    </a>
  );
}

function QuestionCard({ config, body }) {
  const Icon = config.icon;
  if (!body) return null;

  return (
    <div
      className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-xl transition hover:-translate-y-0.5 hover:bg-white/[0.05] sm:p-6"
      style={{ boxShadow: `0 20px 60px -42px ${config.accent}` }}
    >
      <div
        className="pointer-events-none absolute -right-14 -top-14 h-40 w-40 rounded-full opacity-25 blur-3xl transition group-hover:opacity-40"
        style={{ background: config.accent }}
      />
      <div className="relative flex items-start gap-4">
        <span
          className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl border"
          style={{
            color: config.accent,
            borderColor: `${config.accent}55`,
            background: `${config.accent}18`,
          }}
        >
          <Icon size={18} />
        </span>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold leading-snug text-white">
            {config.label}
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-white/60">{body}</p>
        </div>
      </div>
    </div>
  );
}

function VideoCard({ video }) {
  const videoId = typeof video?.id === "string" ? video.id : video?.id?.videoId;
  const thumbnail =
    video?.snippet?.thumbnails?.high?.url ||
    video?.snippet?.thumbnails?.medium?.url ||
    video?.snippet?.thumbnails?.default?.url;

  if (!videoId || !thumbnail) return null;

  return (
    <a
      href={`https://www.youtube.com/watch?v=${videoId}`}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative block overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] transition hover:-translate-y-1 hover:border-white/20 hover:bg-white/[0.05]"
      style={{ boxShadow: "0 20px 60px -36px rgba(16,185,129,0.65)" }}
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={thumbnail}
          alt={video?.snippet?.title || "Creator video"}
          className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#07060d] via-black/20 to-transparent" />
        <div className="absolute inset-0 grid place-items-center">
          <span
            className="grid h-12 w-12 place-items-center rounded-full text-black transition group-hover:scale-105"
            style={{ background: MINT, boxShadow: `0 0 30px ${MINT}80` }}
          >
            <Play size={20} fill="currentColor" />
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-white">
          {video?.snippet?.title || "Untitled video"}
        </h3>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-white/45">
          <span>{video?.statistics?.viewCount || "N/A"} views</span>
          {video?.snippet?.publishedAt && (
            <>
              <span>•</span>
              <span>{new Date(video.snippet.publishedAt).toLocaleDateString()}</span>
            </>
          )}
        </div>
      </div>
    </a>
  );
}

function LoadingState() {
  return (
    <main className="min-h-screen bg-[#07060d] px-4 py-10 text-white sm:px-6">
      <div className="mx-auto max-w-6xl space-y-4">
        <div className="h-80 animate-pulse rounded-3xl border border-white/10 bg-white/[0.04]" />
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="h-40 animate-pulse rounded-3xl border border-white/10 bg-white/[0.04]" />
          <div className="h-40 animate-pulse rounded-3xl border border-white/10 bg-white/[0.04]" />
        </div>
      </div>
    </main>
  );
}

export default function CreatorProfile({ creatorName }) {
  const [creator, setCreator] = useState(null);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);
  const [videos, setVideos] = useState([]);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCreator = async () => {
      try {
        const profiles = await listPublicProfiles({ username: creatorName });
        const userData = profiles[0];

        if (userData) {
          setUserId(userData.id);
          setCreator(userData);

          if (userData.videoLinks) {
            fetchVideoData(Object.values(userData.videoLinks));
          }
        } else {
          setError("No creator found with that ID");
        }
      } catch (err) {
        setError("Failed to fetch data or insufficient permissions");
        console.error("Error fetching creator profile:", err);
      }
    };

    const fetchVideoData = async (urls) => {
      try {
        const videoData = await Promise.all(
          urls.map(async (url) => {
            const videoId = extractYouTubeId(url);
            if (!videoId) return null;

            const response = await fetch(
              `https://www.googleapis.com/youtube/v3/videos?id=${videoId}&key=${process.env.REACT_APP_YOUTUBE_API_KEY}&part=snippet,contentDetails,statistics`
            );
            const data = await response.json();

            if (data.items && data.items.length > 0) {
              return data.items[0];
            }

            return null;
          })
        );

        setVideos(videoData.filter((video) => video !== null));
      } catch (err) {
        console.error("Error fetching video data:", err);
      }
    };

    const extractYouTubeId = (url) => {
      const regExp =
        /^https:\/\/www\.youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})(?=$|[&])/;
      const match = url?.match(regExp);
      return match ? match[1] : null;
    };

    fetchCreator();
  }, [creatorName]);

  const createChat = async () => {
    if (!currentUser || !userId) {
      toast.error("You must be logged in");
      navigate("/login");
      return;
    }

    try {
      const chatsRef = collection(db, "chats");
      const q = query(
        chatsRef,
        where("members", "array-contains", currentUser.uid)
      );
      const querySnapshot = await getDocs(q);

      let existingChatId = null;

      querySnapshot.forEach((chatDoc) => {
        const data = chatDoc.data();
        if (data.members.includes(userId)) {
          existingChatId = chatDoc.id;
        }
      });

      if (existingChatId) {
        navigate({
          pathname: "/chat",
          search: `?conversationId=${existingChatId}`,
        });
        return;
      }

      const currentUserDoc = await getDoc(doc(db, "Users", currentUser.uid));
      const currentUserInfo = currentUserDoc.data();
      const publicFriendInfo = creator;
      const chatKey = `${currentUser.uid}_${userId}`;

      await setDoc(doc(db, "chats", chatKey), {
        members: [currentUser.uid, userId],
        lastMessage: "",
        timestamp: new Date(),
        Users: [
          {
            uid: currentUser.uid,
            username: currentUserInfo?.profileInfo?.username || "",
            profilePicUrl: currentUserInfo?.profileInfo?.profilePicUrl || "",
            name: `${currentUserInfo?.profileInfo?.firstName || ""} ${
              currentUserInfo?.profileInfo?.lastName || ""
            }`,
          },
          {
            uid: userId,
            username: publicFriendInfo?.profileInfo?.username || "",
            profilePicUrl: publicFriendInfo?.profileInfo?.profilePicUrl || "",
          },
        ],
      });

      await setDoc(doc(db, "Users", currentUser.uid, "chats", chatKey), {
        chatId: chatKey,
        timestamp: new Date(),
      });

      await setDoc(doc(db, "Users", userId, "chats", chatKey), {
        chatId: chatKey,
        timestamp: new Date(),
      });

      const friendUserDoc = await getDoc(doc(db, "Users", userId));
      const friendUserInfo = friendUserDoc.data();
      await queueTransactionalEmail({
        to: friendUserInfo.profileInfo.email,
        email: transactionalEmails.chatActivity({
          senderName: currentUserInfo?.profileInfo?.username || "A Kre8 member",
          conversationId: chatKey,
        }),
      });

      navigate({
        pathname: "/chat",
        search: `?conversationId=${chatKey}`,
      });
    } catch (err) {
      console.error("Error creating or checking chat:", err);
      toast.error("We could not start this conversation. Please try again.");
    }
  };

  if (error) {
    return (
      <>
        <SEO
          title="Creator Profile Not Found | Kre8"
          description="This Kre8 creator profile could not be found."
          canonicalPath={`/creator/${creatorName}`}
          noindex
        />
        <main className="min-h-screen bg-[#07060d] px-4 py-10 text-white sm:px-6">
          <div className="mx-auto max-w-6xl rounded-3xl border border-red-400/20 bg-red-500/10 p-5 text-sm text-red-100">
            {error}
          </div>
        </main>
      </>
    );
  }

  if (!creator) {
    return <LoadingState />;
  }

  const profile = creator.profileInfo || {};
  const questions = profile.questions || {};
  const visibleQuestions = QUESTION_CARDS.filter((item) => questions[item.key]);
  const username = profile.username || creatorName || "creator";
  const mainGame =
    typeof profile.mainGame === "object"
      ? profile.mainGame?.categoryName || profile.mainGame?.name
      : profile.mainGame;
  const title = `@${username} | Creator Profile on Kre8`;
  const description =
    profile.tagline ||
    profile.about ||
    `View @${username}'s Kre8 creator profile, content style, editing needs, and top videos.`;
  const sameAs = SOCIALS.map((item) => creator.socials?.[item.key]);
  const entitlements = getUserEntitlements(creator);
  const sortedVideos = [...videos].sort(
    (a, b) =>
      Number(b?.statistics?.viewCount || 0) -
      Number(a?.statistics?.viewCount || 0)
  );

  return (
    <>
      <SEO
        title={title}
        description={description}
        canonicalPath={`/creator/${username}`}
        image={profile.profilePicUrl || undefined}
        type="profile"
        structuredData={[
          profilePageSchema({
            name: `@${username}`,
            description,
            path: `/creator/${username}`,
            image: profile.profilePicUrl,
            sameAs,
            role: "Content Creator",
            knowsAbout: mainGame,
          }),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Creators", path: "/creators" },
            { name: `@${username}`, path: `/creator/${username}` },
          ]),
        ]}
      />
      <main className="relative min-h-screen overflow-hidden bg-[#07060d] text-white">
      <div
        className="pointer-events-none absolute -top-32 left-1/2 h-[420px] w-[780px] -translate-x-1/2 rounded-full opacity-20 blur-[130px]"
        style={{ background: `radial-gradient(circle, ${PURPLE}, transparent 60%)` }}
      />
      <div
        className="pointer-events-none absolute right-0 top-80 h-[380px] w-[520px] rounded-full opacity-16 blur-[120px]"
        style={{ background: `radial-gradient(circle, ${MINT}, transparent 60%)` }}
      />

      <section className="relative mx-auto max-w-6xl px-4 pb-10 pt-8 sm:px-6 sm:pt-12">
        <div
          className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-xl sm:p-8 lg:p-10"
          style={{ boxShadow: `0 24px 90px -50px ${PURPLE}` }}
        >
          <div
            className="pointer-events-none absolute -left-28 -top-28 h-72 w-72 rounded-full opacity-25 blur-3xl"
            style={{ background: PURPLE }}
          />
          <div
            className="pointer-events-none absolute -bottom-28 right-0 h-72 w-72 rounded-full opacity-20 blur-3xl"
            style={{ background: MINT }}
          />

          <div className="relative flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-10">
            <div className="flex flex-col items-center text-center lg:w-[280px] lg:items-start lg:text-left">
              <div className="relative">
                <div
                  className="absolute inset-0 rounded-full opacity-70 blur-2xl"
                  style={{
                    background: `conic-gradient(from 120deg, ${PURPLE}, ${MINT}, ${PURPLE})`,
                  }}
                />
                <div
                  className="relative rounded-full p-1"
                  style={{ background: `linear-gradient(135deg, ${PURPLE}, ${MINT})` }}
                >
                  <img
                    src={profile.profilePicUrl}
                    alt={`${profile.username || "Creator"} profile`}
                    className="h-28 w-28 rounded-full bg-[#0d0a18] object-cover sm:h-36 sm:w-36"
                    fetchPriority="high"
                    decoding="async"
                    loading="eager"
                  />
                </div>
              </div>

              <div className="mt-5 flex flex-wrap justify-center gap-2 lg:justify-start">
                {profile.role && (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-white/70">
                    <Briefcase size={12} style={{ color: PURPLE }} />
                    {profile.role}
                  </span>
                )}
                {mainGame && (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/25 bg-emerald-500/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-100">
                    <Gamepad2 size={12} />
                    {mainGame}
                  </span>
                )}
                {entitlements.priorityCreatorProfile && (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-purple-300/25 bg-purple-500/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-purple-100">
                    <Sparkles size={12} />
                    Priority Creator
                  </span>
                )}
              </div>
            </div>

            <div className="min-w-0 flex-1 text-center lg:text-left">
              <div className="flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/40 lg:justify-start">
                <Sparkles size={13} style={{ color: MINT }} />
                Creator Profile
              </div>
              <h1 className="mt-2 truncate text-3xl font-extrabold tracking-tight text-white sm:text-5xl">
                @{profile.username || "creator"}
              </h1>
              {profile.tagline && (
                <p className="mt-3 text-base font-medium leading-relaxed text-white/70 sm:text-lg">
                  {profile.tagline}
                </p>
              )}
              {profile.about && (
                <div className="mt-5 rounded-3xl border border-white/10 bg-black/25 p-5 text-left backdrop-blur">
                  <p className="text-xs font-semibold uppercase tracking-wider text-white/40">
                    About
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-white/65">
                    {profile.about}
                  </p>
                </div>
              )}
              <div className="mt-5 flex flex-wrap justify-center gap-2 lg:justify-start">
                {SOCIALS.map((item) => (
                  <SocialButton
                    key={item.key}
                    href={creator.socials?.[item.key]}
                    label={item.label}
                    icon={item.icon}
                    platform={item.key}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={createChat}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-bold uppercase tracking-wider text-black transition active:scale-[0.98] sm:w-auto"
                style={{
                  background: `linear-gradient(135deg, ${MINT}, #34d399)`,
                  boxShadow: `0 16px 44px -16px ${MINT}`,
                }}
              >
                <MessageCircle size={16} />
                Message @{profile.username || "creator"}
              </button>
            </div>
          </div>
        </div>
      </section>

      {visibleQuestions.length > 0 && (
        <section className="relative mx-auto max-w-6xl px-4 pb-12 sm:px-6">
          <div className="mb-5">
            <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">
              Working Together
            </h2>
            <p className="mt-1 text-sm text-white/50">
              The context editors need before starting a project.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {visibleQuestions.map((item) => (
              <QuestionCard
                key={item.key}
                config={item}
                body={questions[item.key]}
              />
            ))}
          </div>
        </section>
      )}

      <section className="relative mx-auto max-w-6xl px-4 pb-20 sm:px-6">
        <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/40">
              <Sparkles size={13} style={{ color: MINT }} />
              Creator content
            </div>
            <h2 className="mt-2 text-2xl font-bold tracking-tight text-white sm:text-3xl">
              {profile.username || "Creator"}&apos;s Top Videos
            </h2>
            <p className="mt-1 text-sm text-white/55">
              A quick snapshot of their style, pacing, and audience.
            </p>
          </div>
        </div>

        {sortedVideos.length > 0 ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {sortedVideos.map((video, index) => (
              <VideoCard key={video?.id?.videoId || video?.id || index} video={video} />
            ))}
          </div>
        ) : (
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 text-center text-white/55 backdrop-blur-xl">
            <Clock className="mx-auto mb-3 text-white/35" size={24} />
            No videos linked yet.
          </div>
        )}
      </section>
      </main>
    </>
  );
}
